/**
 * FloatingText — spawns text that drifts in a direction and fades out.
 *
 * Use for damage numbers, healing, status effects, gold pickups, etc.
 * Game-agnostic: no knowledge of combat, characters, or game state.
 *
 * Renders at Depth.EFFECTS by default so it appears above sprites
 * but below HUD elements.
 */

declare const Phaser: any;

import { Depth } from "../Depth";

export interface FloatingTextOptions {
  /** Text to display. */
  text: string;
  /** World X position. */
  x: number;
  /** World Y position. */
  y: number;
  /** Font size in px. Default 22. */
  fontSize?: number;
  /** Hex color string. Default "#ffffff". */
  color?: string;
  /** Bold text. Default true. */
  bold?: boolean;
  /** Stroke color. Default "#000000". */
  stroke?: string;
  /** Stroke thickness. Default 3. */
  strokeThickness?: number;
  /** Vertical drift in px (negative = up). Default -40. */
  driftY?: number;
  /** Horizontal drift in px. Default 0. */
  driftX?: number;
  /** Duration in ms. Default 1200. */
  duration?: number;
  /** Easing function. Default "Power2". */
  ease?: string;
  /** Depth layer. Default Depth.EFFECTS (100). */
  depth?: number;
}

/**
 * Spawn a floating text that drifts and fades, then self-destructs.
 * Returns the Phaser text object in case callers need a reference.
 */
export function floatingText(scene: any, opts: FloatingTextOptions): any {
  const fontSize = opts.fontSize ?? 22;
  const bold = opts.bold ?? true;
  const duration = opts.duration ?? 1200;

  const txt = scene.add.text(opts.x, opts.y, opts.text, {
    fontFamily: "system-ui, sans-serif",
    fontSize: `${fontSize}px`,
    color: opts.color ?? "#ffffff",
    fontStyle: bold ? "bold" : "normal",
    stroke: opts.stroke ?? "#000000",
    strokeThickness: opts.strokeThickness ?? 3,
  })
    .setOrigin(0.5)
    .setAlpha(1)
    .setDepth(opts.depth ?? Depth.EFFECTS);

  scene.tweens.add({
    targets: txt,
    y: opts.y + (opts.driftY ?? -40),
    x: opts.x + (opts.driftX ?? 0),
    alpha: 0,
    duration,
    ease: opts.ease ?? "Power2",
    onComplete: () => txt.destroy(),
  });

  return txt;
}
